import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ChevronLeft, Bell, Mail, AlertTriangle, Clock, FileWarning, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import NotificationCard from '../components/NotificationCard';

const NotificationsPage = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [actionFlags, setActionFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  // Determine user role for deep linking
  const getUserRole = () => {
    if (user?.is_platform_owner) return 'platform_owner';
    if (user?.is_host) return 'host';
    return 'renter';
  };

  useEffect(() => {
    fetchNotifications();
    if (user?.is_host) {
      fetchActionFlags();
    }
  }, [user]);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications/');
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchActionFlags = async () => {
    try {
      const res = await api.get('/host/action-flags');
      setActionFlags(res.data.flags || []);
    } catch (error) {
      // Not a host or error - silently ignore
      console.error('Error fetching action flags:', error);
    }
  };

  const handleMarkRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
      
      // FIXED: Emit event so navbar badge updates immediately
      window.dispatchEvent(new Event('notification-read'));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.post('/notifications/mark-all-read');
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
      
      // FIXED: Emit event so navbar badge updates immediately
      window.dispatchEvent(new Event('notification-read'));
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const handleActionFlagClick = (flag) => {
    if (flag.action_url) {
      navigate(flag.action_url);
    } else if (flag.booking_id) {
      navigate(`/bookings/${flag.booking_id}`);
    }
  };

  const totalBadgeCount = unreadCount + actionFlags.length;
  const criticalFlagsCount = actionFlags.filter(f => f.severity === 'critical').length;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-100 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-semibold">Notifications</h1>
            {totalBadgeCount > 0 && (
              <Badge className={criticalFlagsCount > 0 ? "bg-red-600 text-white" : "bg-red-100 text-red-800"}>
                {totalBadgeCount} {unreadCount > 0 ? 'unread' : 'items'}
              </Badge>
            )}
          </div>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" onClick={handleMarkAllRead}>
              Mark all read
            </Button>
          )}
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 md:px-8 py-8 space-y-6">
        {/* Action Flags Section - Only for hosts */}
        {actionFlags.length > 0 && (
          <Card className={`border-2 ${criticalFlagsCount > 0 ? 'border-red-300 bg-red-50/50' : 'border-amber-300 bg-amber-50/50'}`}>
            <CardHeader className="pb-2">
              <CardTitle className="font-heading flex items-center gap-2 text-base">
                <AlertTriangle className={`w-5 h-5 ${criticalFlagsCount > 0 ? 'text-red-500' : 'text-amber-500'}`} />
                Action Required
                <Badge className={criticalFlagsCount > 0 ? 'bg-red-600' : 'bg-amber-500'}>
                  {actionFlags.length}
                </Badge>
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">
                These items require your attention to complete bookings or maintain compliance
              </p>
            </CardHeader>
            <CardContent className="space-y-2">
              {actionFlags.map((flag, idx) => (
                <button
                  key={`flag-${idx}`}
                  onClick={() => handleActionFlagClick(flag)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    flag.severity === 'critical'
                      ? 'bg-red-50 border-red-200 hover:bg-red-100'
                      : 'bg-amber-50 border-amber-200 hover:bg-amber-100'
                  }`}
                  data-testid={`action-flag-${idx}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                      {flag.type.includes('posthire') ? (
                        <Clock className={`w-4 h-4 mt-0.5 ${flag.severity === 'critical' ? 'text-red-600' : 'text-amber-600'}`} />
                      ) : (
                        <FileWarning className={`w-4 h-4 mt-0.5 ${flag.severity === 'critical' ? 'text-red-600' : 'text-amber-600'}`} />
                      )}
                      <div>
                        <p className={`text-sm font-medium ${flag.severity === 'critical' ? 'text-red-800' : 'text-amber-800'}`}>
                          {flag.message}
                        </p>
                        <p className={`text-xs mt-0.5 ${flag.severity === 'critical' ? 'text-red-600' : 'text-amber-600'}`}>
                          Click to take action
                        </p>
                      </div>
                    </div>
                    <ChevronRight className={`w-4 h-4 flex-shrink-0 ${flag.severity === 'critical' ? 'text-red-400' : 'text-amber-400'}`} />
                  </div>
                </button>
              ))}
            </CardContent>
          </Card>
        )}

        {/* Notifications Section */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="font-heading flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#004B8D]" />
              Notifications
              {unreadCount > 0 && (
                <Badge variant="outline" className="ml-2 bg-blue-50 text-blue-700 border-blue-200">
                  {unreadCount} unread
                </Badge>
              )}
            </CardTitle>
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">
                Email notifications active via SendGrid
              </Badge>
              <span className="text-xs text-slate-500">In-app notifications active</span>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#004B8D]"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-12">
                <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No notifications yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {notifications.map((notification) => (
                  <NotificationCard
                    key={notification.notification_id}
                    notification={notification}
                    onMarkRead={handleMarkRead}
                    currentUserRole={getUserRole()}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          Click any notification to view details. Email delivery coming soon.
        </p>
      </div>
    </div>
  );
};

export default NotificationsPage;
